import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, Zap, BarChart3, Users, ArrowRight, CheckCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useCMSContent } from '@/hooks/use-cms-content';
import { fetchPlatformPage, type PlatformPageData } from '@/lib/cms';
import BreadcrumbJsonLd from '@/components/BreadcrumbJsonLd';

const capabilities = [
  {
    id: 'builder',
    icon: Layers,
    title: 'Interactive Ad Builder',
    description: 'Create stunning interactive ads without code or complex production workflows.',
    features: [
      'Drag-and-drop interface',
      '50+ pre-built templates',
      'Pre-coded interactive components',
      'Real-time preview',
      'Brand asset management',
      'Responsive design tools',
    ],
    gradient: 'from-[#635bff] to-purple-500',
    image: 'https://media.adventive.com/site/application-images/adbuilder-screenshot.png',
  },
  {
    id: 'delivery',
    icon: Zap,
    title: 'Creative Delivery & Trafficking',
    description: 'Seamlessly deliver campaigns across every major ad server and platform.',
    features: [
      '40+ ad server integrations',
      'One-click trafficking',
      'Universal tag generation',
      'Mid-flight creative updates',
      'Version control',
      'Automated QA',
    ],
    gradient: 'from-cyan-500 to-blue-500',
    image: 'https://media.adventive.com/site/application-images/campaigns-screenshot.png',
  },
  {
    id: 'insights',
    icon: BarChart3,
    title: 'Performance Insights',
    description: 'Track engagement, viewability, and outcomes in one unified dashboard.',
    features: [
      'Real-time analytics',
      'Engagement heatmaps',
      'Viewability tracking',
      'Conversion attribution',
      'Custom reporting',
      'Data export',
    ],
    gradient: 'from-emerald-500 to-teal-500',
    image: 'https://media.adventive.com/site/application-images/analytics-screenshot.png',
  },
  {
    id: 'managed',
    icon: Users,
    title: 'Managed Services',
    description: 'Expert support for teams that need help creating and optimizing campaigns.',
    features: [
      'Dedicated account team',
      'Creative production support',
      'Campaign optimization',
      'Strategic consulting',
      'Training and onboarding',
      'Priority support',
    ],
    gradient: 'from-amber-500 to-orange-500',
    image: 'https://d64gsuwffb70l.cloudfront.net/69408eb499b5789ef7367cf9_1766000491648_c198019a.png',
  },
];

const Platform: React.FC = () => {
  const navigate = useNavigate();
  const fetchFn = useCallback(() => fetchPlatformPage('platform'), []);
  const { data: cms } = useCMSContent<PlatformPageData | null>('platform-page', fetchFn, null);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0f]">
      <BreadcrumbJsonLd crumbs={[
        { name: 'Home', item: '/' },
        { name: 'Platform' },
      ]} />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 grid-pattern" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] opacity-30 dark:opacity-40">
            <div className="w-full h-full bg-gradient-to-br from-[#635bff] via-[#00d4ff] to-transparent rounded-full blur-3xl" />
          </div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#635bff]/10 dark:bg-[#635bff]/20 border border-[#635bff]/20 text-sm font-semibold text-[#635bff] mb-6">
              Platform
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 dark:text-white leading-tight">
              {cms?.heroHeadline || 'Everything you need to'}{' '}
              <span className="stripe-gradient-text">{cms?.heroHeadlineGradient || 'create, deliver, and optimize'}</span>{' '}
              interactive ads.
            </h1>
            <p className="mt-6 text-xl text-slate-600 dark:text-slate-400">
              {cms?.heroSubheadline || 'A modular platform that grows with your needs—from self-service tools to full managed support.'}
            </p>
            <div className="mt-10">
              <button
                onClick={() => navigate('/demo')}
                className="stripe-button inline-flex items-center px-8 py-4 text-base font-semibold text-white rounded-full group"
              >
                Request Demo
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      {capabilities.map((capability, index) => (
        <section
          key={capability.id}
          id={capability.id}
          className="py-24 lg:py-32 relative overflow-hidden"
        >
          <div className="absolute inset-0">
            <div className="absolute inset-0 grid-pattern" />
            {index % 2 === 1 && (
              <div className="absolute inset-0 bg-slate-50/50 dark:bg-white/[0.02]" />
            )}
            <div className={`absolute ${index % 2 === 0 ? 'right-0' : 'left-0'} top-1/2 -translate-y-1/2 w-[500px] h-[500px] opacity-20 dark:opacity-30`}>
              <div className={`w-full h-full bg-gradient-to-br ${capability.gradient} rounded-full blur-3xl`} />
            </div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${capability.gradient} text-white mb-6 shadow-lg`}>
                  <capability.icon className="w-7 h-7" />
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight">
                  {capability.title}
                </h2>
                <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
                  {capability.description}
                </p>
                <ul className="mt-8 grid sm:grid-cols-2 gap-4">
                  {capability.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${capability.gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
                        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-slate-700 dark:text-slate-300 font-medium">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="relative">
                  <div className={`absolute -inset-4 bg-gradient-to-r ${capability.gradient} opacity-20 rounded-3xl blur-2xl`} />
                  <div className="relative bg-white dark:bg-white/5 rounded-2xl shadow-2xl shadow-slate-900/10 dark:shadow-black/30 overflow-hidden border border-slate-200/50 dark:border-white/10 backdrop-blur-sm">
                    <img
                      src={capability.image}
                      alt={capability.title}
                      className="w-full h-auto object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#635bff] via-[#7c3aed] to-[#00d4ff]" />
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />
          <div className="absolute top-0 right-0 w-[500px] h-[500px] opacity-30">
            <div className="w-full h-full bg-gradient-to-br from-white/20 to-transparent rounded-full blur-3xl" />
          </div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">
            {cms?.ctaHeadline || 'Ready to see the platform in action?'}
          </h2>
          <p className="mt-6 text-lg text-white/80 max-w-2xl mx-auto">
            {cms?.ctaSubheadline || 'Get a personalized demo and see how Adventive can transform your advertising.'}
          </p>
          <div className="mt-10">
            <button
              onClick={() => navigate('/demo')}
              className="inline-flex items-center px-8 py-4 text-base font-semibold text-[#635bff] bg-white rounded-full hover:bg-slate-100 transition-all hover:shadow-xl group"
            >
              Request Demo
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Platform;
